/** 

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

import { useState, useRef } from 'react';

// @mui material components
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Icon from '@mui/material/Icon';

// Vision UI Dashboard PRO React components
import VuiBox from 'components/VuiBox';
import VuiTypography from 'components/VuiTypography';
import VuiButton from 'components/VuiButton';
import VuiInput from 'components/VuiInput';
import VuiSelect from 'components/VuiSelect';

// Vision UI Dashboard PRO React example components
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

// React icons
import { IoAdd, IoHelpCircle } from 'react-icons/io5';

// API Services
import { takedownsApi } from 'services/takedownsApi';

function SubmitContent() {
  const [urls, setUrls] = useState(['']);
  const [formData, setFormData] = useState({
    infringementType: '',
    contentUrl: '',
    additionalNotes: '',
    files: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const fileInputRef = useRef(null);

  const infringementTypes = [
    'Explicit',
    'False Endorsement',
    'NILV (Non-explicit)',
    'Unlicensed Merchandise',
    'Trademark',
    'Copyright',
    'Other'
  ];

  const handleAddUrl = () => {
    setUrls([...urls, '']);
  };

  const handleUrlChange = (index, value) => {
    const newUrls = [...urls];
    newUrls[index] = value;
    setUrls(newUrls);
    setFormData({ ...formData, contentUrl: newUrls.join(', ') });
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFileUpload = (files) => {
    setFormData({ ...formData, files: [...formData.files, ...files] });
  };

  // Function to save uploaded files to user_submitted_content directory
  const saveUploadedFiles = async (files) => {
    const savedFiles = [];
    
    // Determine the upload server URL based on current hostname
    // If accessing via EC2 or external IP, use the same host
    // Otherwise, use localhost for local development
    const currentHost = window.location.hostname;
    const uploadServerUrl = currentHost === 'localhost' || currentHost === '127.0.0.1'
      ? 'http://localhost:3011/api/upload/user-content'
      : `http://${currentHost}:3011/api/upload/user-content`;
    
    
    for (const file of files) {
      try {
        
        // Create a unique filename with timestamp
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const uniqueFileName = `${timestamp}_${file.name}`;
        
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('filename', uniqueFileName);
        
        
        // Upload file to the file upload server
        const response = await fetch(uploadServerUrl, {
          method: 'POST',
          body: formData,
        });
        
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to upload file: ${response.statusText}`);
        }
        
        const result = await response.json();
        
        savedFiles.push({
          originalName: file.name,
          uniqueName: uniqueFileName,
          filePath: result.fileInfo.filePath,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified
        });
        
      } catch (err) {
        // Continue without failing the entire submission
      }
    }
    
    return savedFiles;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    setSubmissionResult(null);

    try {
      // Validate form data
      if (!formData.infringementType) {
        throw new Error('Please select an infringement type');
      }

      // Filter out empty URLs and validate
      const validUrls = urls.filter(url => url.trim() !== '');
      if (validUrls.length === 0) {
        throw new Error('Please provide at least one content URL');
      }

      // Validate URLs format
      const urlRegex = /^https?:\/\/.+/;
      const invalidUrls = validUrls.filter(url => !urlRegex.test(url));
      if (invalidUrls.length > 0) {
        throw new Error('Please provide valid URLs (starting with http:// or https://)');
      }

      // Save uploaded files (if any)
      let savedFiles = [];
      if (formData.files.length > 0) {
        savedFiles = await saveUploadedFiles(formData.files);
      } else {
      }

      // Prepare API request data
      const apiData = {
        url: validUrls.join(','), // Join multiple URLs with comma
        infringementType: formData.infringementType,
        additionalNotes: formData.additionalNotes || ''
      };


      // Call the API
      const result = await takedownsApi.submitNewContent(
        apiData.url,
        apiData.infringementType,
        apiData.additionalNotes
      );


      // Handle successful submission
      setSubmissionResult(result);
      setSuccess(true);
      
      // Reset form
      setUrls(['']);
      setFormData({
        infringementType: '',
        contentUrl: '',
        additionalNotes: '',
        files: []
      });
      
      // Clear any file upload components
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach(input => {
        input.value = '';
      });

    } catch (err) {
      setError(err.message || 'Failed to submit content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3} sx={{ overflow: "visible" }}>
        {/* Header */}
        <VuiBox mb={3}>
          <VuiTypography variant="h3" color="white" fontWeight="bold">
            Submit Content
          </VuiTypography>
        </VuiBox>

        {/* Success Message */}
        {success && submissionResult && (
          <VuiBox mb={3}>
            <Alert 
              severity="success" 
              sx={{ 
                backgroundColor: 'rgba(76, 175, 80, 0.1)', 
                color: 'white',
                border: '1px solid rgba(76, 175, 80, 0.3)'
              }}
            >
              <VuiTypography variant="body2" fontWeight="bold" mb={1}>
                Content submitted successfully!
              </VuiTypography>
              <VuiTypography variant="caption" color="white">
                Total URLs submitted: {submissionResult.totalSubmitted || 0}
                {submissionResult.insertedIds && submissionResult.insertedIds.length > 0 && (
                  <span> | Content IDs: {submissionResult.insertedIds.join(', ')}</span>
                )}
              </VuiTypography>
            </Alert>
          </VuiBox>
        )}

        {/* Error Message */}
        {error && (
          <VuiBox mb={3}>
            <Alert 
              severity="error" 
              sx={{ 
                backgroundColor: 'rgba(255, 107, 107, 0.1)', 
                color: 'white',
                border: '1px solid rgba(255, 107, 107, 0.3)'
              }}
            >
              {error}
            </Alert>
          </VuiBox>
        )}

        {/* Main Form Card */}
        <Card sx={{ overflow: "visible" }}>
          <VuiBox p={3}>
            <form onSubmit={handleSubmit}>
              {/* Infringement Type */}
              <VuiBox mb={3}>
                <VuiTypography 
                  variant="button" 
                  color="white" 
                  fontWeight="bold" 
                  mb={1}
                  sx={{ fontSize: '12px', textTransform: 'uppercase' }}
                >
                  INFRINGEMENT TYPE
                </VuiTypography>
                <VuiSelect
                  id="infringement-type"
                  value={formData.infringementType ? { value: formData.infringementType, label: formData.infringementType } : null}
                  onChange={(selectedOption) => handleInputChange('infringementType', selectedOption ? selectedOption.value : '')}
                  options={infringementTypes.map((type) => ({
                    value: type,
                    label: type
                  }))}
                  placeholder="Select infringement type"
                  sx={{
                    '& .react-select__control': {
                      backgroundColor: '#0f1535',
                      border: '0.5px solid rgba(226, 232, 240, 0.3)',
                      borderRadius: '15px',
                      minHeight: '48px',
                      '&:hover': {
                        border: '0.5px solid rgba(226, 232, 240, 0.5)',
                      },
                    },
                    '& .react-select__value-container': {
                      padding: '0 16px',
                    },
                    '& .react-select__single-value': {
                      color: 'white',
                    },
                    '& .react-select__placeholder': {
                      color: '#a0aec0',
                    },
                    '& .react-select__indicator-separator': {
                      backgroundColor: 'rgba(226, 232, 240, 0.3)',
                    },
                    '& .react-select__dropdown-indicator': {
                      color: 'white',
                    },
                    '& .react-select__menu': {
                      backgroundColor: '#0f1535',
                      border: '0.5px solid rgba(226, 232, 240, 0.3)',
                      borderRadius: '15px',
                    },
                    '& .react-select__option': {
                      backgroundColor: 'transparent',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 117, 255, 0.1)',
                      },
                      '&--is-selected': {
                        backgroundColor: '#0075ff',
                      },
                    },
                  }}
                />
              </VuiBox>

              {/* Content URLs */}
              <VuiBox mb={3}>
                <VuiTypography 
                  variant="button" 
                  color="white" 
                  fontWeight="bold" 
                  mb={1}
                  sx={{ fontSize: '12px', textTransform: 'uppercase' }}
                >
                  CONTENT URL
                </VuiTypography>
                {urls.map((url, index) => (
                  <VuiBox key={index} display="flex" alignItems="center" gap={1} mb={1}>
                    <VuiInput
                      placeholder="https://www..."
                      value={url}
                      onChange={(e) => handleUrlChange(index, e.target.value)}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#0f1535',
                          border: '0.5px solid rgba(226, 232, 240, 0.3)',
                          borderRadius: '15px',
                          color: 'white',
                          '& fieldset': {
                            border: 'none',
                          },
                          '&:hover fieldset': {
                            border: 'none',
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none',
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: 'white',
                          '&::placeholder': {
                            color: '#a0aec0',
                          },
                        },
                      }}
                    />
                    {index === urls.length - 1 && (
                      <IconButton
                        onClick={handleAddUrl}
                        sx={{
                          backgroundColor: '#0075ff',
                          color: 'white',
                          borderRadius: '50%',
                          width: '30px',
                          height: '30px',
                          '&:hover': {
                            backgroundColor: '#0056cc',
                          },
                        }}
                      >
                        <IoAdd size="16px" />
                      </IconButton>
                    )}
                  </VuiBox>
                ))}
              </VuiBox>

              {/* Additional Notes */}
              <VuiBox mb={3}>
                <VuiTypography 
                  variant="button" 
                  color="white" 
                  fontWeight="bold" 
                  mb={1}
                  sx={{ fontSize: '12px', textTransform: 'uppercase' }}
                >
                  ADDITIONAL NOTES (optional)
                </VuiTypography>
                <TextField
                  multiline
                  rows={1}
                  fullWidth={true}
                  placeholder="Provide any additional context of the content"
                  value={formData.additionalNotes}
                  onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                  variant="outlined"
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0f1535',
                      border: '0.5px solid rgba(226, 232, 240, 0.3)',
                      borderRadius: '15px',
                      color: 'white',
                      padding: '0',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: 'white',
                      padding: '16px !important',
                      width: '100% !important',
                      //minHeight: '80px',
                      '&::placeholder': {
                        color: '#a0aec0',
                      },
                    },
                    '& .MuiInputBase-inputMultiline': {
                      padding: '16px !important',
                      width: '100% !important',
                      //minHeight: '80px',
                      resize: 'vertical',
                    },
                    '& .MuiInputBase-root': {
                      padding: '0 !important',
                    },
                  }}
                />
              </VuiBox>

              {/* File Upload Dropzone */}
              <VuiBox mb={3}>
                <VuiBox display="flex" alignItems="center" gap={1} mb={1}>
                  <VuiTypography 
                    variant="button" 
                    color="white" 
                    fontWeight="bold" 
                    sx={{ fontSize: '12px', textTransform: 'uppercase' }}
                  >
                    INFRINGING MEDIA/FILE (optional)
                  </VuiTypography>
                  <Tooltip 
                    title="Optional - You may upload images, videos, audio files, or other documents that you suspect of infringement."
                    arrow
                    placement="top"
                  >
                    <IconButton size="small" sx={{ color: '#a0aec0' }}>
                      <IoHelpCircle size="16px" />
                    </IconButton>
                  </Tooltip>
                </VuiBox>
                <VuiBox
                  sx={{
                    backgroundColor: '#0f1535',
                    border: '2px dashed rgba(226, 232, 240, 0.3)',
                    borderRadius: '15px',
                    minHeight: '120px',
                    padding: '20px',
                    textAlign: 'center',
                    color: '#a0aec0',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#0075ff',
                      backgroundColor: 'rgba(0, 117, 255, 0.05)',
                    },
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const files = Array.from(e.dataTransfer.files);
                    if (files.length > 0) {
                      handleFileUpload(files);
                    }
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".png,.jpg,.jpeg,.gif,.bmp,.webp,.mp4,.avi,.mov,.wmv,.flv,.webm,.mp3,.wav,.flac,.aac,.ogg,.pdf,.doc,.docx,.txt"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 0) {
                        handleFileUpload(files);
                      }
                    }}
                  />
                  
                  {formData.files.length === 0 ? (
                    <VuiBox>
                      <Icon sx={{ fontSize: '48px', color: '#a0aec0', mb: 2 }}>cloud_upload</Icon>
                      <VuiTypography variant="body2" color="text" mb={1}>
                        Drop files here or click to upload
                      </VuiTypography>
                      <VuiTypography variant="caption" color="text">
                        Supported: Images, Videos, Audio, PDF, Documents, Text files (Max 50MB per file)
                      </VuiTypography>
                    </VuiBox>
                  ) : (
                    <VuiBox>
                      <VuiTypography variant="body2" color="white" mb={2} fontWeight="bold">
                        {formData.files.length} file(s) selected:
                      </VuiTypography>
                      {formData.files.map((file, index) => (
                        <VuiBox 
                          key={index} 
                          display="flex" 
                          alignItems="center" 
                          justifyContent="space-between" 
                          mb={1}
                          p={1}
                          sx={{
                            backgroundColor: 'rgba(0, 117, 255, 0.1)',
                            borderRadius: '8px',
                          }}
                        >
                          <VuiBox display="flex" alignItems="center" gap={1}>
                            <Icon sx={{ color: '#0075ff' }}>insert_drive_file</Icon>
                            <VuiTypography variant="caption" color="white">
                              {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </VuiTypography>
                          </VuiBox>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFormData(prev => ({
                                ...prev,
                                files: prev.files.filter((_, i) => i !== index)
                              }));
                            }}
                            sx={{ color: '#ff6b6b', '&:hover': { backgroundColor: 'rgba(255, 107, 107, 0.1)' } }}
                          >
                            <Icon>close</Icon>
                          </IconButton>
                        </VuiBox>
                      ))}
                      <VuiTypography variant="caption" color="text" mt={2} display="block">
                        Click or drop more files to add them
                      </VuiTypography>
                    </VuiBox>
                  )}
                </VuiBox>
              </VuiBox>

              {/* Submit Button */}
              <VuiBox display="flex" justifyContent="center" mt={4}>
                <VuiButton
                  type="submit"
                  variant="contained"
                  color="info"
                  disabled={loading}
                  sx={{
                    backgroundColor: loading ? '#4a5568' : '#0075ff',
                    padding: '12px 40px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    borderRadius: '12px',
                    '&:hover': {
                      backgroundColor: loading ? '#4a5568' : '#0056cc',
                    },
                    '&:disabled': {
                      backgroundColor: '#4a5568',
                      color: '#a0aec0',
                    },
                  }}
                >
                  {loading ? (
                    <VuiBox display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} color="inherit" />
                      SUBMITTING...
                    </VuiBox>
                  ) : (
                    'SUBMIT'
                  )}
                </VuiButton>
              </VuiBox>
            </form>
          </VuiBox>
        </Card>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SubmitContent;
